import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { appRoutes } from '../../constants/appRoutes';
import { demoSettingsStore } from '../store/demoSettingsStore';
import { DemoCommand } from '../types/demoConsts';
import { useAddInitialElementsToMap } from './useAddInitialElementsToMap';
import { useAddTreatmentReviewStage } from './useAddTreatmentReviewStage';
import { useAddFlowMetadataElement } from './useAddFlowMetadataElement';
import { useResetHealthcareFunnels } from './useResetHealthcareFunnels';
import { useRenameFunnelMapToDental } from './useRenameFunnelMapToDental';

export const useHandleDreamforceDemo = () => {
  const location = useLocation();
  const { dreamforceDemo } = useFeatureToggle();

  // Demo action hooks
  const { addInitialElementsToMap } = useAddInitialElementsToMap();
  const { addTreatmentReviewStage } = useAddTreatmentReviewStage();
  const { addTreatmentFlowMetadata } = useAddFlowMetadataElement();
  const { resetHealthcareFunnels } = useResetHealthcareFunnels();
  const { renameToDentalPatientManagement } = useRenameFunnelMapToDental();

  const handleDemoCommand = useCallback(async (messageStr: string) => {
    // Check if demo is enabled and we're on the right route
    if (!dreamforceDemo || !location.pathname.includes(appRoutes.funnelEditor.route)) {
      return false; // Not handled
    }

    // Check for demo commands
    const detectedCommand = demoSettingsStore.getCommandForMessage(messageStr);

    if (!detectedCommand) {
      return false; // Not handled
    }

    console.log(`🎯 Demo: Detected command "${detectedCommand}" in message`);

    // Get current delay configuration
    const delayConfiguration = demoSettingsStore.getDelayConfiguration();

    try {
      switch (detectedCommand) {
        case DemoCommand.ADD_HEALTHCARE_FUNNELS:
          console.log(`🎯 Demo: Executing Add Healthcare Funnels (delay: ${delayConfiguration[DemoCommand.ADD_HEALTHCARE_FUNNELS]}s)`);
          setTimeout(async () => {
            try {
              // First, rename the funnel map to set the proper name before adding elements
              await renameToDentalPatientManagement();
              console.log('🎯 Demo: Funnel map renamed to "Dental Patient Management"');
              
              // Brief wait for Redux state to propagate
              console.log('🎯 Demo: Waiting for Redux state to propagate...');
              await new Promise(resolve => setTimeout(resolve, 500)); // 500ms wait for Redux update
              
              // Then add all the healthcare elements
              await addInitialElementsToMap();
              console.log('🎯 Demo: Add Healthcare Funnels completed');
            } catch (error) {
              console.error('🎯 Demo: Error in delayed Add Healthcare Funnels execution:', error);
            }
          }, delayConfiguration[DemoCommand.ADD_HEALTHCARE_FUNNELS] * 1000);
          break;

        case DemoCommand.ADD_TREATMENT_REVIEW_STAGE:
          console.log(`🎯 Demo: Executing Add Treatment Review Stage (delay: ${delayConfiguration[DemoCommand.ADD_TREATMENT_REVIEW_STAGE]}s)`);
          setTimeout(() => {
            try {
              addTreatmentReviewStage();
              console.log('🎯 Demo: Add Treatment Review Stage completed');
            } catch (error) {
              console.error('🎯 Demo: Error in delayed Add Treatment Review Stage execution:', error);
            }
          }, delayConfiguration[DemoCommand.ADD_TREATMENT_REVIEW_STAGE] * 1000);
          break;

        case DemoCommand.ADD_METADATA_AND_CONNECT:
          console.log(`🎯 Demo: Executing Add Treatment Flow Metadata & Connect (delay: ${delayConfiguration[DemoCommand.ADD_METADATA_AND_CONNECT]}s)`);
          setTimeout(() => {
            try {
              addTreatmentFlowMetadata();
              console.log('🎯 Demo: Add Treatment Flow Metadata & Connect completed');
            } catch (error) {
              console.error('🎯 Demo: Error in delayed Add Treatment Flow Metadata & Connect execution:', error);
            }
          }, delayConfiguration[DemoCommand.ADD_METADATA_AND_CONNECT] * 1000);
          break;

        case DemoCommand.RESET_DEMO:
          console.log('🎯 Demo: Executing Reset Demo (no delay)');
          await resetHealthcareFunnels();
          break;

        default:
          console.warn('🎯 Demo: Unknown command detected:', detectedCommand);
          return false;
      }

      console.log('🎯 Demo: Command scheduled/executed successfully');
      return true; // Handled successfully

    } catch (error) {
      console.error('🎯 Demo: Error executing command:', error);
      return false; // Error occurred
    }
  }, [
    dreamforceDemo,
    location.pathname,
    addInitialElementsToMap,
    addTreatmentReviewStage,
    addTreatmentFlowMetadata,
    resetHealthcareFunnels,
    renameToDentalPatientManagement,
  ]);

  return {
    handleDemoCommand,
    isDemoEnabled: dreamforceDemo && location.pathname.includes(appRoutes.funnelEditor.route),
  };
};
